/**
 * @file fieldGroups
 *
 * @fileoverview Helpers for field group information and ordering for entity forms.
 */

import JobTypeEnum from '~/jobiqo/enums/jobType';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { EntityDefinition, EntityField } from '~/jobiqo/models/entity';
import { FlaggedField } from '~/jobiqo/models/flaggedField';
import { EntityFieldGroup, FieldGroupInfo } from '~/jobiqo/models/forms';
import { excludedFields } from './excludedFields';
/**
 * This is a map that defines all possible bundles that have
 * runtime configuration. The map is used to check flagged fields
 * defined in the appropriatee configuration.
 */
const runtimeConfigBundleMap = {
  JOBS: [
    'customer',
    JobTypeEnum.JOB_PER_FILE,
    JobTypeEnum.JOB_PER_LINK,
    JobTypeEnum.JOB_PER_TEMPLATE,
    'job_searches',
    'job_application',
  ],
  ORGANIZATIONS: [
    'organization',
    'org_further_locations',
    'org_videos',
    'org_faq',
    'organization_profile',
    'org_contact_persons',
  ],
  RESUME: ['field_resume_edu', 'resume', 'field_resume_xp', 'resume_searches'],
};

/**
 * Get all field keys passed to this form.
 */
export const getGroupsFieldKeys = (
  fieldGroups: FieldGroupInfo[],
  definition: EntityDefinition,
  excludedFieldList: string[],
): string[] => {
  let keys: string[] = [];
  let groupsInfo;
  if (fieldGroups && fieldGroups.length > 0) {
    groupsInfo = fieldGroups;
  } else {
    groupsInfo = orderFieldGroups(
      fieldGroups,
      definition.fields,
      excludedFieldList,
    ).map((group) => {
      return { ...group, fields: group.fields.map((f) => f.id) };
    });
  }

  // All keys within all groups.
  // If no group was passed we still use groups (all in one).
  groupsInfo.forEach((group) => {
    keys = [...keys, ...group.fields];
  });

  return keys;
};

/**
 * Organize all fields within groups as provided by props.
 *
 * @param {EntityField[]} fields
 *   The list of fields to organize.
 *
 * @return {EntityFieldGroup}
 *   The list of fields organized by groups.
 */
export const orderFieldGroups = (
  fieldGroups: FieldGroupInfo[],
  fields: EntityField[],
  excludedFieldList: string[],
): EntityFieldGroup[] => {
  if (fieldGroups.length === 0) {
    // When there is no group information we put all fields inside an empty group.
    return [
      {
        key: 'full',
        name: '',
        description: '',
        icon: '',
        fields: fields.filter(
          (field) =>
            !excludedFieldList.includes(field.id) &&
            !excludedFields.includes(field.id),
        ),
      },
    ];
  }

  // Create a copy of fieldGroups to avoid mutating the original.
  const sortedFieldGroups = [...fieldGroups];

  // Remove groups with order from their original positions.
  sortedFieldGroups.forEach((orderedGroup) => {
    const defaultIndex = sortedFieldGroups.indexOf(orderedGroup);
    // check if the group has an order property set.
    let groupOrder = orderedGroup?.order;

    // If the group has an order property we need to remove it from the original position.
    if (groupOrder !== undefined) {
      sortedFieldGroups.splice(defaultIndex, 1);
      // Add the group to the new position.
      sortedFieldGroups.splice(groupOrder, 0, orderedGroup);
    }
  });

  // Add each field as provided in group information into the groups (actual field information).
  return sortedFieldGroups.map((group) => {
    let orderedGroupFields = [...group.fields];

    // Developers can order fields inside a group by the key "fieldsOrderOverrides" so we need to prioritize this
    // ordering. We also need to make sure to delete from where the field was before.
    if (
      group.fieldsOrderOverrides &&
      Object.keys(group.fieldsOrderOverrides)?.length > 0
    ) {
      Object.keys(group.fieldsOrderOverrides).forEach((fieldOverride) => {
        const originalConfigIndexOfElement = orderedGroupFields.indexOf(
          fieldOverride,
        );
        let intendedOrderOfElement = group.fieldsOrderOverrides?.[
          fieldOverride
        ]!;

        // If the field was already in the group we need to remove it from the old position.
        if (originalConfigIndexOfElement > -1) {
          orderedGroupFields.splice(originalConfigIndexOfElement, 1);
          intendedOrderOfElement--;
        }
        orderedGroupFields.splice(intendedOrderOfElement, 0, fieldOverride);
      });
    }

    return {
      ...group,
      icon: group.icon ? group.icon : '',
      fields: orderedGroupFields
        .filter(
          (field) =>
            !excludedFieldList.includes(field) &&
            !excludedFields.includes(field),
        )
        .map((groupField) => {
          return fields.filter((field) => groupField === field.id)[0];
        })
        .filter((g) => g),
    };
  });
};

/**
 * Removes appropriate fields based on FLAGGED_FIELDS configuration.
 *
 * @param {FieldGroupInfo[]} fieldGroupInfos
 * The field group infos.
 *
 * @returns {FieldGroupInfo[]}
 *  The field group infos without flagged fields.
 */
export const removeFlaggedFields = (
  fieldGroupInfos: FieldGroupInfo[],
  bundle: string = '',
): FieldGroupInfo[] => {
  const returnInfos: FieldGroupInfo[] = [];
  const frontendConfig = getCurrentFrontendConfig();

  for (let [config, bundles] of Object.entries(runtimeConfigBundleMap)) {
    // Find appropriate config.
    if (bundles.includes(bundle)) {
      fieldGroupInfos.forEach((fieldGroupInfo) => {
        const flaggedFields = (frontendConfig?.[config]?.FLAGGED_FIELDS ??
          []) as FlaggedField[];
        let fieldGroupFields = fieldGroupInfo.fields;

        // Remove every flagged field.
        flaggedFields.forEach((flaggedField) => {
          const [flagName, { form }] = Object.entries(flaggedField)[0];
          const isFieldGroupInfoMatched = form.groupKey === fieldGroupInfo.key;

          if (
            isFieldGroupInfoMatched &&
            !frontendConfig?.[config]?.[flagName]
          ) {
            fieldGroupFields = fieldGroupFields.filter(
              (fieldGroupField) => fieldGroupField !== form.fieldName,
            );
          }
        });

        returnInfos.push({ ...fieldGroupInfo, fields: fieldGroupFields });
      });
    }
  }

  // When no runtime config matches the bundle there are no flagged fields to
  // remove, so keep the original groups instead of discarding them.
  return returnInfos.length > 0 ? returnInfos : fieldGroupInfos;
};

/**
 * Removes fields from a given groups depending on the group
 * hiddenFieldsPerBundle property and a particular bundle.
 *
 * @param {EntityFieldGroup[]} fieldGroups
 * @param {string} bundle
 * @returns
 * Groups without configured fields for a given bundle.
 */
export function filterFieldGroupsPerBundle(
  fieldGroups: FieldGroupInfo[],
  bundle?: string,
): FieldGroupInfo[] {
  if (!bundle) {
    return fieldGroups;
  }

  fieldGroups.forEach((group) => {
    const bundleHiddenFields = group.hiddenFieldsPerBundle?.[bundle];
    if (bundleHiddenFields) {
      bundleHiddenFields.forEach((hiddenField) => {
        const fieldIndex = group.fields.indexOf(hiddenField);
        delete group.fields[fieldIndex];
      });
    }
  });

  return fieldGroups;
}

/**
 * Removes fields from a given filterTarget object depending on the group
 * hiddenFieldsPerBundle property and a particular bundle.
 *
 * @param {FieldGroupInfo[]} fieldGroups
 * @param {{}} filterTarget
 * @param {string} bundle
 * @returns
 * Object without properties that match configured fields for
 * a given bundle.
 */
export function filterHiddenFieldsPerBundle(
  fieldGroups: FieldGroupInfo[],
  filterTarget: {},
  bundle?: string,
): {} {
  if (!bundle || !filterTarget) {
    return filterTarget;
  }

  const overridesKeys = Object.keys(filterTarget);

  fieldGroups.forEach((group) => {
    const bundleHiddenFields = group.hiddenFieldsPerBundle?.[bundle];
    if (bundleHiddenFields) {
      bundleHiddenFields.forEach((hiddenField) => {
        if (overridesKeys.includes(hiddenField)) {
          delete filterTarget[hiddenField];
        }
      });
    }
  });

  return filterTarget;
}
